import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, Pencil, ReceiptText, X } from "lucide-react";
import { Button, Input, Switch } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useOvernightConfig, useSaveOvernightConfig, type OvernightConfig } from "@/hooks/useOvernightConfig";
import { SectionCard, SectionHeader } from "./primitives";
import { shouldShowOvernightToggle } from "./overnightVisibility";

type Draft = { isEnabled: boolean; checkoutTime: string; price: number | null };

const formatCents = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_DRAFT: Draft = { isEnabled: false, checkoutTime: "", price: null };

const toDraft = (config: OvernightConfig): Draft => ({
  isEnabled: config.isEnabled,
  checkoutTime: config.checkoutTime ?? "",
  price: config.price,
});

export default function OvernightSection({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation("daycareHotel");
  const { data: config, isLoading } = useOvernightConfig();
  const save = useSaveOvernightConfig();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [priceCents, setPriceCents] = useState(0);
  const priceInputRef = useRef<HTMLInputElement | null>(null);
  // When the query is idle (no clinic yet) or failed, fall back to an empty
  // draft instead of rendering the loading skeleton forever.
  const current = draft ?? (config ? toDraft(config) : isLoading ? null : EMPTY_DRAFT);

  useEffect(() => {
    if (config && !isEditing) setDraft(toDraft(config));
  }, [config, isEditing]);

  if (isLoading || !current) {
    return (
      <section>
        <SectionHeader
          tone="overnight"
          badge={t("badgeOvernight")}
          title={t("overnight.title")}
          description={t("overnight.description")}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1].map((index) => (
            <SectionCard key={index} className="h-[136px] animate-pulse bg-muted/40">{null}</SectionCard>
          ))}
        </div>
      </section>
    );
  }

  const update = (patch: Partial<Draft>) => setDraft({ ...current, ...patch });
  const disabled = !canEdit || !current.isEnabled;
  const formattedPrice = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(current.price ?? 0);

  const startEditing = () => {
    setDraft({ ...current });
    setPriceCents(Math.round((current.price ?? 0) * 100));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(config ? toDraft(config) : EMPTY_DRAFT);
    setIsEditing(false);
  };

  const applyPriceCents = (nextCents: number) => {
    const clamped = Math.max(0, Math.min(nextCents, 999999999));
    setPriceCents(clamped);
    update({ price: clamped / 100 });
  };

  const setCaretFromDigitsRight = (digitsRight: number) => {
    requestAnimationFrame(() => {
      const input = priceInputRef.current;
      if (!input) return;
      if (digitsRight <= 0) {
        input.setSelectionRange(input.value.length, input.value.length);
        return;
      }
      let counted = 0;
      let position = input.value.length;
      for (let index = input.value.length - 1; index >= 0; index -= 1) {
        if (/\d/.test(input.value[index])) counted += 1;
        if (counted === digitsRight) {
          position = index;
          break;
        }
      }
      input.setSelectionRange(position, position);
    });
  };

  const handlePriceKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const value = input.value;
    const selectionStart = input.selectionStart ?? value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (["Tab", "Enter", "Escape", "Home", "End"].includes(event.key) || event.key.startsWith("Arrow")) return;

    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      if (selectionEnd > selectionStart) {
        applyPriceCents(0);
        setCaretFromDigitsRight(0);
        return;
      }
      let index = event.key === "Backspace" ? selectionStart - 1 : selectionStart;
      const step = event.key === "Backspace" ? -1 : 1;
      while (index >= 0 && index < value.length && !/\d/.test(value[index])) index += step;
      if (index < 0 || index >= value.length) return;
      const digitsRight = value.slice(index + (event.key === "Backspace" ? 1 : 0)).replace(/\D/g, "").length;
      const centsString = String(priceCents).padStart(3, "0");
      const digitIndex = centsString.length - 1 - digitsRight;
      applyPriceCents(Number(centsString.slice(0, digitIndex) + centsString.slice(digitIndex + 1) || 0));
      setCaretFromDigitsRight(digitsRight);
      return;
    }

    if (event.key.length === 1 && /\d/.test(event.key)) {
      event.preventDefault();
      if (selectionEnd > selectionStart) {
        applyPriceCents(Number(event.key));
        setCaretFromDigitsRight(0);
        return;
      }
      const digitsRight = value.slice(selectionStart).replace(/\D/g, "").length;
      const centsString = String(priceCents).padStart(3, "0");
      const insertAt = centsString.length - digitsRight;
      applyPriceCents(Number(centsString.slice(0, insertAt) + event.key + centsString.slice(insertAt)));
      setCaretFromDigitsRight(digitsRight);
      return;
    }
    if (event.key.length === 1) event.preventDefault();
  };

  const saveEditing = async () => {
    if (!draft) return;
    try {
      await save.mutateAsync({
        isEnabled: draft.isEnabled,
        checkoutTime: draft.checkoutTime || null,
        price: draft.price,
      });
      setIsEditing(false);
      toast({ title: t("overnight.toastUpdated") });
    } catch {
      toast({ variant: "destructive", title: t("overnight.toastSaveFailed") });
    }
  };

  return (
    <section>
      <SectionHeader
        tone="overnight"
        badge={t("badgeOvernight")}
        title={t("overnight.title")}
        hint={t("overnight.hint")}
        description={t("overnight.description")}
        action={
          <div className="flex items-center gap-2">
            {shouldShowOvernightToggle(isEditing) ? (
              <Switch
                checked={current.isEnabled}
                onCheckedChange={(checked) => update({ isEnabled: checked })}
                label={t("overnight.enabled")}
                disabled={!canEdit}
              />
            ) : null}
            {canEdit && !isEditing ? (
              <Button type="button" variant="outline" size="sm" onClick={startEditing}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                {t("common.edit")}
              </Button>
            ) : null}
          </div>
        }
      />
      <div className={cn("grid gap-4 sm:grid-cols-2 lg:grid-cols-3", !current.isEnabled && "opacity-50")}>
        <SectionCard className="p-4">
          <div className="mb-2 flex items-center gap-2 text-[12.5px] text-muted-foreground">
            <Clock className="h-4 w-4 text-akuna-purple" aria-hidden="true" />
            <label htmlFor="overnight-checkout-time">{t("overnight.checkoutTimeLabel")}</label>
          </div>
          <div className="flex items-center gap-2.5">
            {isEditing ? (
              <Input
                id="overnight-checkout-time"
                type="time"
                value={current.checkoutTime}
                onChange={(event) => update({ checkoutTime: event.target.value })}
                disabled={disabled}
                className="w-32 font-display text-lg font-extrabold"
              />
            ) : (
              <span className="flex w-18 items-center px-3 font-display text-xl font-extrabold">
                {current.checkoutTime || "—"}
              </span>
            )}
            <span className="text-[12.5px] text-color-light">{t("overnight.checkoutTimeSuffix")}</span>
          </div>
          <p className="mt-2 text-xs text-color-light">{t("overnight.checkoutTimeHint")}</p>
        </SectionCard>

        <SectionCard className="p-4">
          <div className="mb-2 flex items-center gap-2 text-[12.5px] text-muted-foreground">
            <ReceiptText className="h-4 w-4 text-akuna-orange" aria-hidden="true" />
            <label htmlFor="overnight-price">{t("overnight.priceLabel")}</label>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-extrabold text-foreground">
              {t("overnight.currencySymbol")}
            </span>
            {isEditing ? (
              <Input
                ref={priceInputRef}
                id="overnight-price"
                type="text"
                inputMode="numeric"
                value={priceCents > 0 ? formatCents(priceCents) : ""}
                onKeyDown={handlePriceKeyDown}
                onChange={() => {}}
                aria-label={t("overnight.priceLabel")}
                className="w-32 font-display text-lg font-extrabold"
                placeholder="0,00"
              />
            ) : (
              <span className="font-display text-xl font-extrabold text-foreground">{formattedPrice}</span>
            )}
          </div>
          <p className="mt-2 text-xs text-color-light">{t("overnight.priceHint")}</p>
        </SectionCard>
      </div>
      {isEditing ? (
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={cancelEditing} disabled={save.isPending}>
            <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("common.cancel")}
          </Button>
          <Button type="button" size="sm" onClick={saveEditing} disabled={save.isPending}>
            <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
